/* eslint-disable sonarjs/cognitive-complexity */
import styled from "@emotion/styled";
import { Tooltip } from "@ui/components/common/tooltip";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import { colorWithAlpha } from "@utils/color-utils";
import { Chart as ChartRef } from "chart.js";
import { interpolate } from "popmotion";
import { Chart, ChartProps } from "primereact/chart";
import React, { Fragment, useCallback, useMemo, useRef, useState } from "react";

export interface Scale {
	min: number;
	tick?: number;
	max: number;
}

type UiTheme = "cold" | "hot" | "hot-reverse";

interface GraphProps {
	id: string;
	scale: { x: Scale; y: Scale };
	data: (number | null)[];
	displayScales?: boolean;
	uiTheme?: UiTheme;
	displayLineOnHover?: boolean;
	formatValue?: (yValue: number) => string;
	renderDotTooltipContent?: (xValue: number, yValue: number | null) => string | JSX.Element;
	formatScaleYTooltip?: (yValue: number) => string;
	formatScaleY?: (yValue: number) => string | JSX.Element;
	formatScaleX?: (xValue: number) => string;
	onDataHover?: (dataIndex: number) => void;
	onDotTooltipHide?: (dataIndex: number) => void;
}

export const Graph = React.forwardRef<{ getChart: () => ChartRef }, GraphProps>(
	(
		{
			id,
			scale,
			data,
			displayScales = true,
			uiTheme = "cold",
			displayLineOnHover,
			formatValue,
			renderDotTooltipContent,
			formatScaleYTooltip,
			formatScaleY,
			formatScaleX,
			onDataHover,
			onDotTooltipHide,
		},
		ref,
	) => {
		const canvasContextRef = useRef<CanvasContext | null>(null);
		const [tooltip, setTooltip] = useState<TooltipData>({
			position: { x: 0, y: 0 },
			displayed: false,
			data: { dataIndex: 0, yValue: data[0] ?? scale.y.min },
		});

		const xTicks = scale.x.tick ?? scale.x.max - scale.x.min + 1;
		const yTicks = scale.y.tick ?? scale.y.max - scale.y.min + 1;

		const formatYValue = useCallback((v: number) => formatValue?.(v) ?? v.toString(), [formatValue]);
		const xAxisData = useMemo(() => Array.from({ length: xTicks }, (_, i) => scale.x.min + i), [scale.x.min, xTicks]);

		const graphData = useMemo(
			() => getGraphData(xAxisData, data, canvasContextRef, uiTheme, scale.y),
			[xAxisData, data, uiTheme, scale.y],
		);

		const graphOptions = useMemo(() => {
			const changeTooltip = (t: TooltipData) => {
				if (displayScales) {
					setTooltip(t);
					if (t.displayed) {
						onDataHover?.(t.data.dataIndex);
					} else {
						onDotTooltipHide?.(t.data.dataIndex);
					}
				}
			};
			return getGraphOptions(scale.y, displayScales, formatYValue, changeTooltip, formatScaleX);
		}, [displayScales, formatScaleX, formatYValue, onDataHover, onDotTooltipHide, scale.y]);

		const plugins = useMemo(() => (displayLineOnHover ? drawVerticalLinePlugin() : undefined), [displayLineOnHover]);

		const renderTooltip = useCallback(() => {
			if (!renderDotTooltipContent) {
				return null;
			}
			const ratio = tooltip.data.dataIndex / xTicks;
			const tooltipPosition = getTooltipPosition(ratio);
			return (
				<StyledTooltip
					visible={tooltip.displayed}
					content={renderDotTooltipContent(xAxisData[tooltip.data.dataIndex], data[tooltip.data.dataIndex])}
					placement={tooltipPosition}
				>
					<StyledCircle data={tooltip}></StyledCircle>
				</StyledTooltip>
			);
		}, [data, renderDotTooltipContent, tooltip, xAxisData, xTicks]);

		return (
			<GraphSectionContent className={`graph-${id}`} displayScales={displayScales}>
				<InternalGraph
					ref={ref}
					data={graphData}
					options={graphOptions}
					plugins={plugins}
					displayScales={displayScales}
				/>
				{displayScales && formatScaleYTooltip && (
					<ScaleY>
						{Array.from({ length: yTicks }, (_, i) => scale.y.min + (i * (scale.y.max - scale.y.min)) / (yTicks - 1))
							.reverse()
							.map(value => (
								<Fragment key={`${id}-${value}`}>
									<Tooltip content={formatScaleYTooltip(value)} placement="right" allowHTML>
										<ScaleYTick visible={!!formatScaleY} className={`p-size-xs scale-y-${id}-${value}`} key={value}>
											{formatScaleY?.(value) ?? formatYValue(value)}
										</ScaleYTick>
									</Tooltip>
								</Fragment>
							))}
					</ScaleY>
				)}
				{renderTooltip()}
			</GraphSectionContent>
		);
	},
);
Graph.displayName = "Graph";

const InternalGraph = React.memo(
	React.forwardRef<{ getChart: () => ChartRef }, { displayScales: boolean } & ChartProps>((props, ref) => (
		<GraphContent ref={ref as React.ForwardedRef<Chart>} id="chart" type="line" {...props} />
	)),
);
InternalGraph.displayName = "InternalGraph";

const GraphSectionContent = styled.div<{ displayScales: boolean }>`
	position: relative;
	height: 244px;
	margin-top: ${({ displayScales }) => (displayScales ? 24 : 30)}px;
	margin-left: ${({ displayScales }) => (displayScales ? 24 : 64)}px;
	margin-right: 24px;
	margin-bottom: ${({ displayScales }) => (displayScales ? 16 : 46)}px;
`;

const GraphContent = styled(Chart)`
	position: relative;
	height: 100%;
	width: 100%;

	canvas {
		position: relative;
		width: 100% !important;
		height: 100% !important;
	}
`;

const ScaleY = styled.div`
	position: absolute;
	margin-left: -16px;
	width: 40px;
	top: 6px;
	left: 0;
	height: 207px;
	${stack(undefined, "spacebetween", "center")};
`;

const ScaleYTick = styled.div<{ visible?: boolean }>`
	opacity: ${({ visible }) => (visible ? 1 : 0)};
	cursor: pointer;
	line-height: 1;
`;

type TooltipData = {
	position: { x: number; y: number };
	data: { dataIndex: number; yValue: number };
	displayed: boolean;
};

const getTooltipPosition = (ratio: number): "right" | "left" | "bottom" => {
	if (ratio < 0.33) {
		return "right";
	}

	if (ratio > 0.67) {
		return "left";
	}

	return "bottom";
};

const StyledTooltip = styled(Tooltip)`
	white-space: pre-line;
`;

const CIRCLE_RADIUS = 8;
const StyledCircle = styled.div<{ data: TooltipData }>`
	left: ${({ data }) => data.position.x - CIRCLE_RADIUS}px;
	top: ${({ data }) => data.position.y - CIRCLE_RADIUS}px;
	position: absolute;
	width: ${CIRCLE_RADIUS * 2}px;
	height: ${CIRCLE_RADIUS * 2}px;
	pointer-events: none;
	touch-action: none;
	border-radius: ${CIRCLE_RADIUS}px;
`;

type CanvasContext = {
	width: number;
	height: number;
	backgroundGradient: CanvasGradient;
	lineGradient: CanvasGradient;
};
type Area = { right: number; left: number; bottom: number; top: number };

function getNewCanvasContext(
	ctx: CanvasRenderingContext2D,
	chartArea: Area,
	currentContext: CanvasContext | null,
	uiTheme: UiTheme,
): CanvasContext {
	const chartWidth = chartArea.right - chartArea.left;
	const chartHeight = chartArea.bottom - chartArea.top;

	if (currentContext?.width !== chartWidth || currentContext.height !== chartHeight) {
		// Create the gradient because this is either the first render
		// or the size of the chart has changed
		const gradientColor = colors.primary25;
		const firstColor = uiTheme === "hot" ? colors.primary : colors.rejection;
		const secondColor = uiTheme === "hot" ? colors.rejection : colors.primary;

		const backgroundGradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
		if (uiTheme === "cold") {
			backgroundGradient.addColorStop(0, colorWithAlpha(gradientColor, 0));
			backgroundGradient.addColorStop(1, colorWithAlpha(gradientColor, 0.4));
		} else {
			backgroundGradient.addColorStop(0, colorWithAlpha(firstColor, 0));
			backgroundGradient.addColorStop(1, colorWithAlpha(secondColor, 0.16));
		}

		const lineGradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
		lineGradient.addColorStop(0, colorWithAlpha(firstColor, 0.6));
		lineGradient.addColorStop(1, colorWithAlpha(secondColor, 0.6));

		return { backgroundGradient, lineGradient, width: chartWidth, height: chartHeight };
	}

	return currentContext;
}

type InsideContext = {
	dataIndex: number;
	dataset: { data: number[] };
	chart: { ctx: CanvasRenderingContext2D; chartArea: Area | null };
};

function getGraphData(
	xAxisLabels: number[],
	data: (number | null)[],
	canvasContextRef: React.MutableRefObject<CanvasContext | null>,
	uiTheme: UiTheme,
	yScale: Scale,
) {
	const getPointColor = (context: InsideContext) => {
		const index = context.dataIndex;
		const value = context.dataset.data[index];
		const firstColor = uiTheme === "hot" ? colors.primary : colors.rejection;
		const secondColor = uiTheme === "hot" ? colors.rejection : colors.primary;

		return uiTheme === "cold"
			? colors.primary
			: interpolate([yScale.min, yScale.max], [firstColor, secondColor])(value);
	};

	const emptyDataSet = data.includes(null)
		? data.map(v => (v === null ? (yScale.max - yScale.min) / 2 + yScale.min : null))
		: null;

	return {
		labels: xAxisLabels,
		datasets: [
			{
				data,
				borderColor:
					uiTheme === "cold"
						? colors.primary25
						: (context: InsideContext) => {
								const { ctx, chartArea } = context.chart;
								if (!chartArea) {
									// This case happens on initial chart load
									return;
								}
								const newContext = getNewCanvasContext(ctx, chartArea, canvasContextRef.current, uiTheme);
								canvasContextRef.current = newContext;
								return newContext.lineGradient;
							},
				tension: 0.4,
				backgroundColor: (context: InsideContext) => {
					const { ctx, chartArea } = context.chart;
					if (!chartArea) {
						// This case happens on initial chart load
						return;
					}
					const newContext = getNewCanvasContext(ctx, chartArea, canvasContextRef.current, uiTheme);
					canvasContextRef.current = newContext;
					return newContext.backgroundGradient;
				},
				fill: true,
				borderWidth: 4,
				pointStyle: "circle",
				pointRadius: 6,
				pointHoverRadius: 8,
				pointHitRadius: 12,
				pointBackgroundColor: getPointColor,
				pointHoverBackgroundColor: colors.white,
				pointHoverBorderColor: getPointColor,
				pointBorderColor: colors.white,
				pointBorderWidth: 1,
				pointHoverBorderWidth: 2,
			},
			...(!emptyDataSet
				? []
				: [
						{
							data: emptyDataSet,
							borderWidth: 4,
							pointStyle: "circle",
							pointRadius: 6,
							pointHoverRadius: 8,
							pointHitRadius: 12,
							pointBackgroundColor: colors.grey50,
							pointHoverBackgroundColor: colors.grey50,
							pointBorderWidth: 0,
							showLine: false,
						},
					]),
		],
	};
}

function getGraphOptions(
	scaleY: Scale,
	displayScales: boolean,
	formatValue: (yValue: number) => string,
	onTooltipChange: (data: TooltipData) => void,
	formatScaleX?: (xIndex: number) => string,
) {
	return {
		maintainAspectRatio: false,
		aspectRatio: 0.3,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: false,
				external: ({
					tooltip,
				}: {
					tooltip: {
						opacity: number;
						caretX: number;
						caretY: number;
						dataPoints: { parsed: { x: number; y: number } }[];
					};
				}) => {
					onTooltipChange({
						position: { x: tooltip.caretX, y: tooltip.caretY },
						displayed: tooltip.opacity !== 0,
						data: { dataIndex: tooltip.dataPoints[0].parsed.x, yValue: tooltip.dataPoints[0].parsed.y },
					});
				},
			},
		},
		scales: {
			x: {
				ticks: {
					display: displayScales,
					color: colors.grey,
					font: {
						size: 12,
						family: fonts.body,
						weight: "600",
					},
					padding: 8,
					callback: formatScaleX ? (index: number) => formatScaleX(index) : undefined,
					maxRotation: 0,
				},
				grid: {
					display: false,
				},
				border: {
					display: false,
				},
			},
			y: {
				ticks: {
					display: displayScales,
					color: colors.black,
					padding: 8,
					crossAlign: "far",
					showLabelBackdrop: true,
					count: scaleY.tick ?? scaleY.max - scaleY.min + 1,
					font: {
						size: 12,
						family: fonts.body,
						weight: "600",
					},
					callback: (value: number) => formatValue(value),
				},
				grid: {
					color: colors.grey25,
					lineWidth: 2,
					drawTicks: false,
				},
				border: {
					dash: [6, 16],
					display: false,
				},
				suggestedMin: scaleY.min,
				suggestedMax: scaleY.max,
			},
		},
	};
}

function drawVerticalLinePlugin() {
	const drawVerticalLine = (chart: {
		ctx: CanvasRenderingContext2D;
		tooltip: { _active: { element: DOMRect }[] };
		scales: { y: DOMRect };
	}) => {
		if (chart.tooltip._active.length) {
			const x = chart.tooltip._active[0].element.x;
			const yAxis = chart.scales.y;
			const ctx = chart.ctx;
			ctx.save();
			ctx.beginPath();
			ctx.moveTo(x, yAxis.top);
			ctx.lineTo(x, yAxis.bottom);
			ctx.lineWidth = 2;
			ctx.strokeStyle = colors.grey25;
			ctx.stroke();
			ctx.restore();
		}
	};

	return [{ beforeDraw: drawVerticalLine }];
}
